import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import DocumentModal from '../common/DocumentModal.js';
import userService from '../../services/UserService.js';
import { Users, Pencil, ArrowLeft, FileText, Eye } from "lucide-react";

const ViewUser = () => {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUser();
  }, [userId]);

  const fetchUser = async () => {
    try {
      const response = await userService.getById(userId);
      setUser(response.data);
    } catch (err) {
      Alert.error('Failed to load user details');
      console.error(err);
      navigate('/users');
    } finally {
      setLoading(false);
    }
  };

  const getProfileImageUrl = () => {
    if (user.profile_photo_base64) {
      return `data:image/jpeg;base64,${user.profile_photo_base64}`;
    }
    return userService.getProfilePhoto(userId);
  };

  const getDocumentUrl = () => {
    return userService.getDocumentAttachment(userId);
  };

  const openDocumentModal = () => {
    setIsDocModalOpen(true);
  };

  const closeDocumentModal = () => {
    setIsDocModalOpen(false);
  };

  const capitalizeStatus = (status) => {
    return status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : '';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50 overflow-hidden">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header />
          <main className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
              <p className="text-sm sm:text-base text-gray-600">Loading user details...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
              <div className="max-w-8xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-4">
                  <div>
                    <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                      <Users className="w-5 h-5 text-buttonBg" />User Details
                    </h1>
                  </div>
                  <div className='w-full lg:w-auto flex flex-col sm:flex-row gap-2 sm:gap-3'>
                    <button
                      onClick={() => navigate(`/users/edit/${user.user_id}`)}
                      className="
                          relative overflow-hidden
                          group flex items-center justify-center gap-2
                          px-3 sm:px-5 py-2.5 sm:py-2
                          text-xs sm:text-sm
                          rounded-lg
                          border border-buttonBg
                          bg-white text-buttonBg
                          font-medium whitespace-nowrap

                          transition-colors duration-300 ease-out
                          hover:text-white
                        "
                      >
                        {/* ORANGE FILL (COLLAPSE IN ON HOVER) */}
                        <span
                          className="
                            absolute inset-0
                            bg-buttonBg
                            scale-x-0
                            group-hover:scale-x-100
                            origin-center
                            transition-transform duration-300 ease-out
                            z-0
                          "
                        />
                      <Pencil
                              className="
                                w-4 h-4 relative z-10
                                transition-transform duration-300 ease-out
                                group-hover:scale-90
                              "
                            />
                            <span className="relative z-10">Edit User</span>
                      
                    </button>
                    <button
                            onClick={() => navigate('/users')}
                            className="
                              relative overflow-hidden
                              group flex items-center justify-center gap-2
                              px-3 sm:px-3 py-2.5 sm:py-2
                              text-xs sm:text-sm
                              rounded-lg
                              border border-buttonBg
                              bg-buttonBg text-buttonTxt
                              font-medium whitespace-nowrap

                              transition-colors duration-300 ease-out
                              hover:bg-transparent hover:text-buttonBg
                            "
                          >
                            {/* COLLAPSING BACKGROUND */}
                            <span
                              className="
                                absolute inset-0
                                bg-buttonBg
                                scale-x-100
                                group-hover:scale-x-0
                                origin-center
                                transition-transform duration-300 ease-out
                                z-0
                              "
                            />

                            {/* ICON */}
                            <ArrowLeft
                              className="
                                w-4 h-4 relative z-10
                                transition-transform duration-300 ease-out
                                group-hover:scale-90
                              "
                            />

                            <span className="relative z-10">Back to Users</span>
                          </button>
                        </div>
                </div>

                {/* Main Content */}
                <div className="bg-white rounded-sm shadow-sm overflow-hidden">
                  {/* Profile Header Section */}
                  <div className="bg-primary p-3 px-4">
                    <div className="flex flex-col lg:flex-row items-start lg:items-center gap-3 lg:gap-4">
                      <div className="flex-1 min-w-0 flex flex-wrap items-center gap-3">
                        {/* ROLE NAME */}
                        <h2 className="text-xl sm:text-lg md:text-lg font-semibold text-secondary mb-0 break-words">
                          {user.role_name}
                        </h2>
                        {/* STATUS BADGE */}
                        <span
                          className={`px-3 sm:px-2 py-1 sm:py-0.5 rounded-full text-xs sm:text-sm font-semibold ${
                            user.status === 'active'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {capitalizeStatus(user.status)}
                        </span>
                      </div>
                      {/* Action Buttons */}
                      <div className="w-full lg:w-auto flex flex-col sm:flex-row gap-2 sm:gap-3">
                        
                        
                      </div>
                    </div>
                  </div>

                  {/* Details Sections */}
                  <div className="p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
                    {/* Basic Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Basic Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-5 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Username</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{user.username || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Full Name</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{user.name}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Email</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{user.email}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Date of Birth</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(user.dob)}</p>
                        </div>
                        {user.organization_name && (
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Organization Name</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-all">{user.organization_name}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Contact Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Contact Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Contact Number</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{user.contact}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">City</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{user.city}</p>
                        </div>
                        <div className="sm:col-span-2">
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Location/Address</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{user.location}</p>
                        </div>
                      </div>
                    </div>

                    {/* Document Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Document Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Document Type</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">
                            {user.document_type ? user.document_type.replace(/_/g, ' ').toUpperCase() : 'N/A'}
                          </p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Document Number</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{user.document_number || 'N/A'}</p>
                        </div>
                        <div className="sm:col-span-2">
                          <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-2">Document Attachment</label>
                          <button
                              onClick={openDocumentModal}
                              className="
                                relative overflow-hidden
                                group flex items-center justify-center gap-2
                                px-3 sm:px-3 py-2.5 sm:py-2
                                text-xs sm:text-sm
                                rounded-lg
                                border border-buttonBg
                                bg-buttonBg text-buttonTxt
                                font-medium whitespace-nowrap

                                transition-colors duration-300 ease-out
                                hover:bg-transparent hover:text-buttonBg
                              "
                            >
                              {/* COLLAPSING BACKGROUND */}
                              <span
                                className="
                                  absolute inset-0
                                  bg-buttonBg
                                  scale-x-100
                                  group-hover:scale-x-0
                                  origin-center
                                  transition-transform duration-300 ease-out
                                  z-0
                                "
                              />

                              {/* ICON */}
                              <Eye
                                className="
                                  w-4 h-4 relative z-10
                                  transition-transform duration-300 ease-out
                                  group-hover:scale-90
                                "
                              />

                              <span className="relative z-10">View Document</span>
                            </button>
                        </div>
                      </div>
                    </div>

                    {/* Role and Status */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Role and Status
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Role</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{user.role_name}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Status</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">
                            <span className={`px-3 sm:px-2 py-1 sm:py-0.5 rounded-full text-xs sm:text-sm font-semibold ${
                              user.status === 'active' 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {capitalizeStatus(user.status)}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Client Rates — only shown for Client role */}
                    {user.role_name === 'Client' && (
                      <div>
                        <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                          Client Rates
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                          {user.gst_no && (
                            <div>
                              <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">GST No</label>
                              <p className="text-sm sm:text-base text-secondary font-semibold break-all">{user.gst_no}</p>
                            </div>
                          )}
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Tank Cleaning Rates</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-all">{user.tank_cleaning_rates ?? 'N/A'}</p>
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Onhire Rates</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-all">{user.onhire_rates ?? 'N/A'}</p>
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">EI Receipt Rates</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-all">{user.ei_receipt_rates ?? 'N/A'}</p>
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Dry Box Rates</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-all">{user.dry_box_rates ?? 'N/A'}</p>
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">TI Report Rates</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-all">{user.ti_report_rates ?? 'N/A'}</p>
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">CC Email(s)</label>
                            {user.cc_email && Array.isArray(user.cc_email) && user.cc_email.length > 0 ? (
                              <ul className="list-disc list-inside text-sm sm:text-base text-secondary font-semibold">
                                {user.cc_email.map((email, index) => (
                                  <li key={index} className="break-all">{email}</li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-sm sm:text-base text-secondary font-semibold">N/A</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">BCC Email(s)</label>
                            {user.bcc_email && Array.isArray(user.bcc_email) && user.bcc_email.length > 0 ? (
                              <ul className="list-disc list-inside text-sm sm:text-base text-secondary font-semibold">
                                {user.bcc_email.map((email, index) => (
                                  <li key={index} className="break-all">{email}</li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-sm sm:text-base text-secondary font-semibold">N/A</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* System Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        System Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">User ID</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{user.user_id}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Created By</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{user.created_by || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Created At</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(user.created_at)}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Last Updated</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(user.updated_at)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>

      {/* Document Modal */}
      <DocumentModal
        isOpen={isDocModalOpen}
        onClose={closeDocumentModal}
        documentUrl={getDocumentUrl()}
        documentType={user.document_type}
      />
    </div>
  );
};

export default ViewUser;